/** Query/hash param testers can add on any staff URL, including production. */
export const IDLE_TIMEOUT_QUERY_PARAM = 'idleTimeoutSec';
export const IDLE_TIMEOUT_STORAGE_KEY = 'clinio_idle_timeout_sec';

const MIN_OVERRIDE_SECONDS = 5;
const MAX_OVERRIDE_SECONDS = 3600;

export interface IdleTimeoutOverrideIo {
  search?: string;
  hash?: string;
  getItem?: (key: string) => string | null;
  setItem?: (key: string, value: string) => void;
  removeItem?: (key: string) => void;
}

function parseOverrideSeconds(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  if (raw === '0' || raw === 'off') return 0;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  if (n < MIN_OVERRIDE_SECONDS || n > MAX_OVERRIDE_SECONDS) return null;
  return n;
}

/**
 * Drop `idleTimeoutSec` from a return path. The tester override is re-applied
 * when that URL is opened again; carrying it through login armed a short
 * timeout against time already spent signed out.
 */
export function stripIdleTimeoutParam(path: string): string {
  if (!path.includes(IDLE_TIMEOUT_QUERY_PARAM)) return path;
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex + 1) : '';
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const searchIndex = withoutHash.indexOf('?');
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex + 1) : '';

  const searchParams = new URLSearchParams(search);
  const searchHadParam = searchParams.has(IDLE_TIMEOUT_QUERY_PARAM);
  searchParams.delete(IDLE_TIMEOUT_QUERY_PARAM);

  const hashParams = new URLSearchParams(hash);
  const hashHadParam = hashParams.has(IDLE_TIMEOUT_QUERY_PARAM);
  hashParams.delete(IDLE_TIMEOUT_QUERY_PARAM);

  const nextSearch = searchHadParam ? searchParams.toString() : search;
  const nextHash = hashHadParam ? hashParams.toString() : hash;
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${nextHash ? `#${nextHash}` : ''}`;
}

function readParam(search: string, hash: string): string | null {
  const fromSearch = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    IDLE_TIMEOUT_QUERY_PARAM,
  );
  if (fromSearch != null) return fromSearch;
  const hashBody = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!hashBody) return null;
  return new URLSearchParams(hashBody).get(IDLE_TIMEOUT_QUERY_PARAM);
}

function sessionStorageIo(): Pick<IdleTimeoutOverrideIo, 'getItem' | 'setItem' | 'removeItem'> {
  try {
    return {
      getItem: (key) => sessionStorage.getItem(key),
      setItem: (key, value) => {
        sessionStorage.setItem(key, value);
      },
      removeItem: (key) => {
        sessionStorage.removeItem(key);
      },
    };
  } catch {
    return {};
  }
}

/**
 * Drop a leftover tester override. Always clears `localStorage` so an older
 * persist cannot linger after sign-out / sign-in. Session storage is cleared
 * unless the caller injected its own `removeItem`.
 */
export function clearIdleTimeoutOverride(io: Pick<IdleTimeoutOverrideIo, 'removeItem'> = {}): void {
  try {
    if (io.removeItem) {
      io.removeItem(IDLE_TIMEOUT_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(IDLE_TIMEOUT_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(IDLE_TIMEOUT_STORAGE_KEY);
  } catch {
    // leftover localStorage must never keep a short timeout
  }
}

/**
 * Short idle timeout for testers. First hit of `?idleTimeoutSec=15` (or the
 * same hash param) is remembered for this tab via sessionStorage so in-app
 * navigation keeps the override. Sign-out / sign-in clear it. `?idleTimeoutSec=off`
 * also clears it. Never written to localStorage.
 */
export function readIdleTimeoutOverrideSeconds(io: IdleTimeoutOverrideIo = {}): number | null {
  const search = io.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const hash = io.hash ?? (typeof window === 'undefined' ? '' : window.location.hash);
  const storage = {
    ...sessionStorageIo(),
    ...io,
  };

  const fromParam = parseOverrideSeconds(readParam(search, hash));
  if (fromParam === 0) {
    clearIdleTimeoutOverride(storage);
    return null;
  }
  if (fromParam != null) {
    try {
      storage.setItem?.(IDLE_TIMEOUT_STORAGE_KEY, String(fromParam));
    } catch {
      // ignore
    }
    return fromParam;
  }

  try {
    return parseOverrideSeconds(storage.getItem?.(IDLE_TIMEOUT_STORAGE_KEY) ?? null) || null;
  } catch {
    return null;
  }
}

export function applyIdleTimeoutOverride<
  T extends {
    inactivity_timeout_minutes: number;
    warning_before_timeout_minutes: number;
  },
>(settings: T, overrideSeconds: number | null = readIdleTimeoutOverrideSeconds()): T {
  if (overrideSeconds == null) return settings;
  const timeoutMinutes = overrideSeconds / 60;
  const warningMinutes = Math.min(
    settings.warning_before_timeout_minutes,
    Math.max(5 / 60, timeoutMinutes / 3),
  );
  return {
    ...settings,
    inactivity_timeout_minutes: timeoutMinutes,
    warning_before_timeout_minutes: warningMinutes,
  };
}
